import { supabase } from '@/integrations/supabase/client';
import { TimelineData } from '@/types/timeline';
import { Activity, Task } from '@/types/lead';

/**
 * Fetch all timeline data for a lead in a single optimized query
 * Combines activities, status history, tasks, and email logs
 */
export async function getLeadTimeline(leadId: string): Promise<TimelineData> {
  try {
    // Execute all queries in parallel for maximum efficiency
    const [activitiesResult, statusHistoryResult, tasksResult, emailLogsResult] = await Promise.all([
      // Activities with profile information
      supabase
        .from('activities')
        .select(`
          *,
          profiles:performed_by (
            first_name,
            last_name
          )
        `)
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false }),
      
      // Status history
      supabase
        .from('lead_status_history')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false }),
      
      // Tasks (all tasks, we'll filter completed ones in the component)
      supabase
        .from('tasks')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false }),
      
      // Email logs
      supabase
        .from('email_logs')
        .select('*')
        .eq('lead_id', leadId)
        .order('sent_at', { ascending: false })
    ]);

    if (activitiesResult.error) throw activitiesResult.error;
    if (statusHistoryResult.error) throw statusHistoryResult.error;
    if (tasksResult.error) throw tasksResult.error;
    if (emailLogsResult.error) throw emailLogsResult.error;

    // Map activities with performer names
    const activities: Activity[] = (activitiesResult.data || []).map((dbActivity: any) => {
      const performedByName = dbActivity.profiles ? 
        `${dbActivity.profiles.first_name || ''} ${dbActivity.profiles.last_name || ''}`.trim() : 
        undefined;

      return {
        id: dbActivity.id,
        leadId: dbActivity.lead_id,
        type: dbActivity.type,
        title: dbActivity.title,
        description: dbActivity.description,
        metadata: dbActivity.metadata,
        performedBy: performedByName,
        createdAt: new Date(dbActivity.created_at),
      };
    });

    // Map status history
    const statusHistory = (statusHistoryResult.data || []).map((item: any) => ({
      id: item.id,
      leadId: item.lead_id,
      status: item.status,
      notes: item.notes,
      createdBy: item.created_by,
      createdAt: item.created_at,
    }));

    // Map tasks with proper type conversion
    const tasks: Task[] = (tasksResult.data || []).map((dbTask: any) => {
      let reminderTimes: string[] = [];
      if (dbTask.reminder_times) {
        try {
          if (typeof dbTask.reminder_times === 'string') {
            reminderTimes = JSON.parse(dbTask.reminder_times);
          } else if (Array.isArray(dbTask.reminder_times)) {
            reminderTimes = dbTask.reminder_times;
          }
        } catch (e) {
          console.error('Error parsing reminder_times:', e);
        }
      }

      return {
        id: dbTask.id,
        leadId: dbTask.lead_id,
        title: dbTask.title,
        description: dbTask.description,
        dueDate: dbTask.due_date ? new Date(dbTask.due_date) : undefined,
        priority: dbTask.priority,
        status: dbTask.status,
        assignedTo: dbTask.assigned_to,
        createdAt: dbTask.created_at,
        completedAt: dbTask.completed_at || undefined,
        categories: dbTask.categories || [],
        reminderTimes: reminderTimes,
        lastNotificationSent: dbTask.last_notification_sent,
      };
    });

    // Map email logs
    const emailLogs = (emailLogsResult.data || []).map((log: any) => ({
      id: log.id,
      leadId: log.lead_id,
      fromEmail: log.from_email,
      toEmail: log.to_email,
      subject: log.subject,
      htmlContent: log.html_content,
      status: log.status,
      errorMessage: log.error_message,
      sentBy: log.sent_by,
      sentAt: log.sent_at,
      resendEmailId: log.resend_email_id,
    }));

    return {
      activities,
      statusHistory,
      tasks,
      emailLogs,
    };
  } catch (error) {
    console.error('Error fetching lead timeline:', error);
    // Return empty arrays on error
    return {
      activities: [],
      statusHistory: [],
      tasks: [],
      emailLogs: [],
    };
  }
}
